import * as React from 'react';
import { clsx } from 'clsx';

export type TagVariant = 'default' | 'success' | 'error' | 'info' | 'neutral';

export interface TagProps extends React.HTMLAttributes<HTMLSpanElement> {
  /**
   * The visual style variant of the tag
   * @default 'default'
   */
  variant?: TagVariant;
  /**
   * The content of the tag
   */
  children: React.ReactNode;
}

// Arbor Design System tag styles
const tagStyles = {
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 8px',
    borderRadius: '99px', // Pill shape
    fontSize: '11px', // Type 1
    fontWeight: '600',
    gap: '4px',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    lineHeight: '1.2',
  },
  variants: {
    default: {
      backgroundColor: '#fff4e5', // caution-100
      color: '#975a00', // caution-700
    },
    success: {
      backgroundColor: '#e5f9eb', // success-100
      color: '#005a19', // success-800
    },
    error: {
      backgroundColor: '#ffeaea', // destructive-100
      color: '#a62323', // destructive-600
    },
    info: {
      backgroundColor: '#e5f4ff', // info-100
      color: '#003d80', // info-800
    },
    neutral: {
      backgroundColor: '#f8f8f8', // grey-050
      color: '#595959', // grey-600
    },
  },
};

/**
 * Tag component - Arbor Design System
 *
 * A label component for categorization and status indication.
 * Uses pill shape (99px radius) with semantic color variants.
 *
 * @example
 * ```tsx
 * <Tag variant="success">Active</Tag>
 * <Tag variant="error">Overdue</Tag>
 * <Tag variant="info">New</Tag>
 * ```
 */
export const Tag = React.forwardRef<HTMLSpanElement, TagProps>(
  ({ variant = 'default', className, style, children, ...props }, ref) => {
    const variantStyles = tagStyles.variants[variant];

    const combinedStyle: React.CSSProperties = {
      ...tagStyles.base,
      ...variantStyles,
      ...style,
    };

    return (
      <span
        ref={ref}
        className={clsx('arbor-tag', `arbor-tag--${variant}`, className)}
        style={combinedStyle}
        {...props}
      >
        {children}
      </span>
    );
  }
);

Tag.displayName = 'Tag';
